import { useCallback, useMemo, useState } from "react"

export type AsyncAction = {
  /** 요청이 끝날 때까지 true. 버튼의 loading·disabled 에 그대로 쓴다. */
  busy: boolean
  error: unknown
  /** 성공하면 true, 실패하면 false 를 돌려주고 오류를 담아 둔다(던지지 않는다). */
  run: (action: () => Promise<unknown>) => Promise<boolean>
  clearError: () => void
}

/**
 * 사람이 버튼을 눌러 일어나는 쓰기 요청 하나.
 *
 * `busy` 를 세우고, 오류를 담고, 어떻게 끝나든 내리는 `try/finally` 를 화면마다
 * 적지 않게 한다. 성공 여부를 **던지지 않고 돌려주므로**, 부르는 쪽은
 * `if (await run(...)) 닫기` 처럼 이어 쓸 수 있다.
 */
export function useAsyncAction(): AsyncAction {
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<unknown>(null)

  const run = useCallback(async (action: () => Promise<unknown>): Promise<boolean> => {
    setBusy(true)
    setError(null)
    try {
      await action()
      return true
    } catch (err) {
      setError(err)
      return false
    } finally {
      setBusy(false)
    }
  }, [])

  const clearError = useCallback(() => setError(null), [])

  // 값이 그대로면 같은 객체를 돌려준다 — 부르는 쪽이 의존성 배열에 그대로 넣을 수 있게.
  return useMemo(() => ({ busy, error, run, clearError }), [busy, error, run, clearError])
}
